import { BROWSER_STORAGE_SERVICE } from '@epicuro-next/platform/storage';
import { mergeDeep, pick } from '@epicuro-next/utilities';
import { Action, ActionReducer } from '@ngrx/store';

const DEFAULT_PERSIST_DEBOUNCE_MS = 250;

/**
 * Module-level registry of storage keys that have already been initialised.
 *
 * When a state module is imported in multiple lazy-loaded feature modules,
 * Angular creates a new child injector for each one, re-instantiating the
 * meta-reducer factory and producing a fresh closure with `onInit = true`.
 * Without this guard the duplicate closure would re-read from storage and
 * merge stale data over the live in-memory state.
 *
 * The Set lives at module scope so it is shared across all closure instances
 * for the lifetime of the application.
 */
export const initializedKeys = new Set<string>();

/**
 * Additional storage backend config for {@link storageMetaReducer}.
 * Allows persisting a subset of keys to a different storage (e.g. sessionStorage)
 * within the same single meta-reducer instance.
 */
export interface ExtraStorageConfig {
  saveKeys: string[];
  storageKey: string;
  storageService: BROWSER_STORAGE_SERVICE;
}

interface NormalizedStorageConfig {
  saveKeys: string[];
  saveKeySegments: string[][];
  storageKey: string;
  storageService: BROWSER_STORAGE_SERVICE;
}

function getValueBySegments(source: unknown, segments: string[]): unknown {
  if (!source) return undefined;
  return segments.reduce(
    (acc, key) => (acc as Record<string, unknown> | undefined)?.[key],
    source as Record<string, unknown> | undefined,
  );
}

function hasKeysChanged<S>(
  config: NormalizedStorageConfig,
  previousState: S | undefined,
  nextState: S,
): boolean {
  if (!previousState) return true;
  if (config.saveKeySegments.length === 0) return previousState !== nextState;
  return config.saveKeySegments.some(
    (segments) =>
      getValueBySegments(previousState, segments) !==
      getValueBySegments(nextState, segments),
  );
}

function normalizeConfig(config: {
  saveKeys: string[];
  storageKey: string;
  storageService: BROWSER_STORAGE_SERVICE;
}): NormalizedStorageConfig {
  return {
    ...config,
    saveKeySegments: config.saveKeys.map((k) => k.split('.')),
  };
}

// tslint:disable-next-line:cognitive-complexity
export function storageMetaReducer<S extends object, A extends Action = Action>(
  saveKeys: string[],
  browserStorageKey: string,
  storageService: BROWSER_STORAGE_SERVICE,
  /** Optional extra storage backends handled inside the same meta-reducer instance. */
  extraStorageConfigs: ExtraStorageConfig[] = [],
) {
  let onInit = true;
  let lastStateSnapshot: S | undefined;

  // One pending-state slot and debounce timer per storage bucket.
  // Index 0 = primary (localStorage), 1..n = extras.
  const pendingStates: (unknown | undefined)[] = [];
  const persistTimers: (ReturnType<typeof setTimeout> | undefined)[] = [];

  const allConfigs: NormalizedStorageConfig[] = [
    normalizeConfig({
      saveKeys,
      storageKey: browserStorageKey,
      storageService,
    }),
    ...extraStorageConfigs.map(normalizeConfig),
  ];

  const schedulePersist = (index: number, stateToSave: unknown) => {
    pendingStates[index] = stateToSave;
    if (persistTimers[index]) return;

    persistTimers[index] = setTimeout(() => {
      persistTimers[index] = undefined;
      allConfigs[index].storageService.set(
        allConfigs[index].storageKey,
        pendingStates[index] as S,
      );
    }, DEFAULT_PERSIST_DEBOUNCE_MS);
  };

  return (reducer: ActionReducer<S, A>) => {
    return (state: S, action: A): S => {
      const nextState = reducer(state, action);

      if (onInit) {
        onInit = false;

        // If this storage key was already initialised by a previous closure
        // (e.g. the state module was imported in a lazy module after the root
        // injector already set it up), skip the storage merge to avoid
        // overwriting the live in-memory state with stale stored data.
        if (initializedKeys.has(browserStorageKey)) {
          lastStateSnapshot = nextState;
          return nextState;
        }

        // Mark the primary key (and all extras) as initialised before the
        // merge so that any re-entrant dispatch during initialisation is safe.
        allConfigs.forEach((c) => initializedKeys.add(c.storageKey));

        // Merge saved state from every storage backend.
        // Primary (localStorage) is the base; extras (e.g. sessionStorage) are
        // applied last so fresher in-session values win for overlapping keys.
        const mergedState = allConfigs.reduce((acc, config) => {
          const savedState = config.storageService.get(config.storageKey);
          return mergeDeep(acc, savedState ?? {}, {
            overrideArrays: true,
          }) as S;
        }, (nextState ?? {}) as S);

        lastStateSnapshot = mergedState;
        return mergedState;
      }

      if (action.type.startsWith('@ngrx/')) {
        return nextState;
      }

      const previousState = lastStateSnapshot;
      lastStateSnapshot = nextState;

      allConfigs.forEach((config, index) => {
        if (!hasKeysChanged(config, previousState, nextState)) {
          return;
        }

        // save the next state to the application storage.
        // better to use pick with PropertyPath feature to save nested properties
        // if empty array is provided for saveKeys, then all state is cached
        const stateToSave = config.saveKeys.length
          ? pick(nextState, config.saveKeys)
          : nextState;

        schedulePersist(index, stateToSave);
      });

      return nextState;
    };
  };
}
